"use client";
import React, {
  ChangeEvent,
  CSSProperties,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";

import { Search } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useMediaQuery } from "react-responsive";

import { FormInput } from "@/components/ui/Forms";
import { EnumLocation, LocationEnumType } from "@/enums/locationEnum";
import {
  useAutocompleteFilteredOptions,
  useAutocompleteLocationOptions,
} from "@/hooks/useAutocompleteLocationOptions";
import IconArrow from "@/icons/ui/navigation/arrow.svg";
import {
  City,
  District,
  LocalizedName,
  MetroStation,
  Region,
} from "@/services/search.service";
import { Filter } from "@/types/SearchFilter.types";
import Button from "@/ui/Button";
import { CloseButton } from "@/ui/CloseButton";
import {
  type AutocompleteItem,
  AutocompleteList,
} from "@/ui/Forms/AutocompleteList";
import RecommendedCities from "@/ui/SearchPanel/RecommendedCities";
import AutocompleteInputSectionForModal from "@/ui/SearchPanel/SearchModals/SearchLocationNew/AutocompleteInputSectionForModal";
import { cn } from "@/utils/cn";
import { createLogger } from "@/utils/logger";

import styles from "./style.module.scss";

type GetNameType = (name: LocalizedName, defaultName?: boolean) => string;

type SearchTypeProps = {
  selectedFilters: Filter;
  setSelectedFilters: React.Dispatch<React.SetStateAction<Filter>>;
  setSearchTitle: (value: string) => void;
  dealType: string;
  style?: CSSProperties;
  handleSearch: (
    filters?: Filter,
    type?: string,
    scrollToTop?: boolean,
  ) => void;
  regionsListData: Region[]; // Updated to use API data
  searchTitleValue?: string; // Optional - no longer used since FormSmartAutocomplete handles input
  onClosePopup: () => void;
  onSearchTitleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void; // Optional - no longer used
};

const logger = createLogger("search-location-modal");

const SearchLocation = ({
  selectedFilters,
  setSelectedFilters,
  dealType,
  style,
  handleSearch,
  setSearchTitle,
  regionsListData,
  searchTitleValue,
  onClosePopup,
  onSearchTitleChange,
}: SearchTypeProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const defaultLocale = "en";
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });

  const modalRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = useState(searchTitleValue ?? "");
  // Ref to track if we're closing to prevent race conditions with filter updates
  const isClosingRef = useRef<boolean>(false);

  const getName = useMemo<GetNameType>(
    () =>
      (name, defaultName = true) => {
        if (defaultName) {
          return name[defaultLocale] || name[locale];
        }
        return name[locale] || name[defaultLocale];
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale],
  );

  const {
    cityToRegionMap,
    regionMap,
    regionMapWithDistrict,
    regionMapWithMicrodistrict,
    regionMapWithMetroStation,
    regionMapWithStreet,
    cityMap,
    cityMapWithMultidistrict,
    cityMapWithStreet,
    cityMapWithMetroStation,
    districtStreetMap,
    districtMicrodistrictMap,
  } = useMemo(() => {
    const cityToRegionMap = new Map<string, string>();
    const regionMap = new Map<string, City[]>();
    const regionMapWithDistrict = new Map<string, any[]>();
    const regionMapWithMicrodistrict = new Map<string, any[]>();
    const regionMapWithMetroStation = new Map<string, any[]>();
    const regionMapWithStreet = new Map<string, any[]>();
    const cityMap = new Map<string, District[]>();
    const cityMapWithMultidistrict = new Map<string, any[]>();
    const cityMapWithStreet = new Map<string, any[]>();
    const cityMapWithMetroStation = new Map<string, MetroStation[]>();
    const districtStreetMap = new Map<string, any[]>();
    const districtMicrodistrictMap = new Map<string, any[]>();

    regionsListData?.forEach((region) => {
      regionMap.set(getName(region.name), region.cities || []);
      (region.cities || []).forEach((city) => {
        regionMapWithDistrict.set(getName(region.name), city.districts || []);
        cityMap.set(getName(city.name), city.districts || []);
        cityToRegionMap.set(getName(city.name), getName(region.name));

        // Map metro stations for each city
        if (city.metroStations && city.metroStations.length > 0) {
          cityMapWithMetroStation.set(
            getName(city.name),
            city.metroStations || [],
          );
          // Also add to region map for backward compatibility
          const existingStations =
            regionMapWithMetroStation.get(getName(region.name)) || [];
          regionMapWithMetroStation.set(getName(region.name), [
            ...existingStations,
            ...(city.metroStations || []),
          ]);
        }

        (city.districts || []).forEach((district) => {
          regionMapWithMicrodistrict.set(
            getName(region.name),
            district.microdistricts || [],
          );
          regionMapWithStreet.set(getName(region.name), district.streets || []);
          cityMapWithMultidistrict.set(
            getName(city.name),
            district.microdistricts || [],
          );
          cityMapWithStreet.set(getName(city.name), district.streets || []);
          districtStreetMap.set(getName(district.name), district.streets || []);
          districtMicrodistrictMap.set(
            getName(district.name),
            district.microdistricts || [],
          );
        });
      });
    });

    return {
      cityToRegionMap,
      regionMap,
      regionMapWithDistrict,
      regionMapWithMicrodistrict,
      regionMapWithMetroStation,
      regionMapWithStreet,
      cityMap,
      cityMapWithMultidistrict,
      cityMapWithStreet,
      cityMapWithMetroStation,
      districtStreetMap,
      districtMicrodistrictMap,
    };
  }, [regionsListData, getName]);

  const toggleSelection = (
    e: ChangeEvent<HTMLInputElement>,
    list: string[],
    item: string,
    type: LocationEnumType,
  ) => {
    // Prevent filter updates if we're in the process of closing
    if (isClosingRef.current) {
      return;
    }

    const { checked } = e.target;

    setSelectedFilters((prev) => {
      // Helper: remove all values not belonging to selected parent
      const prune = (
        current: string[] | undefined,
        allowed: string[] | undefined,
      ) => current?.filter((el) => allowed?.includes(el)) ?? [];

      const next = { ...prev };

      setSearchTerms(initialSearchTerms);

      switch (type) {
        case EnumLocation.region: {
          next[EnumLocation.region] = checked ? [item] : [];
          next[EnumLocation.city] = [];
          next[EnumLocation.district] = [];
          next[EnumLocation.microdistrict] = [];
          next[EnumLocation.metroStation] = [];
          next[EnumLocation.street] = [];

          return next;
        }

        case EnumLocation.city: {
          if (checked) {
            const region = cityToRegionMap.get(item);
            next[EnumLocation.region] = region ? [region] : [];
            next[EnumLocation.city] = [
              ...new Set([...(prev[EnumLocation.city] ?? []), item]),
            ];

            return next;
          }

          const updatedCities =
            prev[EnumLocation.city]?.filter((el) => el !== item) ?? [];

          next[EnumLocation.city] = updatedCities;

          if (updatedCities.length === 0) {
            next[EnumLocation.region] = [];
          }

          // prune districts belonging to remaining cities
          const allowedDistricts = updatedCities.flatMap(
            (c) => cityMap.get(c)?.map((el) => getName(el.name)) ?? [],
          );

          next[EnumLocation.district] = prune(
            prev[EnumLocation.district],
            allowedDistricts,
          );

          // prune microdistricts belonging only to remaining districts
          const allowedMicro = allowedDistricts.flatMap(
            (d) =>
              districtMicrodistrictMap.get(d)?.map((el) => getName(el.name)) ??
              [],
          );

          next[EnumLocation.microdistrict] = prune(
            prev[EnumLocation.microdistrict],
            allowedMicro,
          );

          // prune metro stations belonging to remaining cities
          const allowedMetroStations = updatedCities.flatMap(
            (c) =>
              cityMapWithMetroStation.get(c)?.map((el) => getName(el.name)) ??
              [],
          );

          next[EnumLocation.metroStation] = prune(
            prev[EnumLocation.metroStation],
            allowedMetroStations,
          );

          return next;
        }

        case EnumLocation.district: {
          if (checked) {
            next[EnumLocation.district] = [
              ...new Set([...(prev[EnumLocation.district] ?? []), item]),
            ];

            return next;
          }

          const updatedDistricts =
            prev[EnumLocation.district]?.filter((el) => el !== item) ?? [];

          next[EnumLocation.district] = updatedDistricts;

          // prune microdistricts belonging only to remaining districts
          const allowedMicro = updatedDistricts.flatMap(
            (d) =>
              districtMicrodistrictMap.get(d)?.map((el) => getName(el.name)) ??
              [],
          );

          next[EnumLocation.microdistrict] = prune(
            prev[EnumLocation.microdistrict],
            allowedMicro,
          );

          // prune streets by remaining districts
          const allowedStreets = updatedDistricts.flatMap(
            (d) =>
              districtStreetMap.get(d)?.map((el) => getName(el.name)) ?? [],
          );

          next[EnumLocation.street] = prune(
            prev[EnumLocation.street],
            allowedStreets,
          );

          return next;
        }

        case EnumLocation.microdistrict: {
          if (checked) {
            next[EnumLocation.microdistrict] = [
              ...new Set([...(prev[EnumLocation.microdistrict] ?? []), item]),
            ];

            return next;
          }

          // removing microdistrict
          next[EnumLocation.microdistrict] =
            prev[EnumLocation.microdistrict]?.filter((el) => el !== item) ?? [];

          // prune streets
          const allowedStreets = next[EnumLocation.microdistrict]?.flatMap(
            (m) =>
              districtMicrodistrictMap.get(m)?.map((el) => getName(el.name)) ??
              [],
          );

          next[EnumLocation.street] = prune(
            prev[EnumLocation.street],
            allowedStreets,
          );

          return next;
        }

        case EnumLocation.metroStation: {
          if (checked) {
            next[EnumLocation.metroStation] = [
              ...new Set([...(prev[EnumLocation.metroStation] ?? []), item]),
            ];

            return next;
          }

          // removing metro station
          next[EnumLocation.metroStation] =
            prev[EnumLocation.metroStation]?.filter((el) => el !== item) ?? [];

          return next;
        }
      }

      // fallback for simple types
      next[type] = checked
        ? [...new Set([...(prev[type] ?? []), item])]
        : (prev[type]?.filter((el) => el !== item) ?? []);

      return next;
    });
  };

  const closeWindow = useCallback(() => {
    // Set flag immediately to prevent any filter updates during close
    isClosingRef.current = true;

    flushSync(() => {
      onClosePopup();
    });

    requestAnimationFrame(() => {
      setSearchValue(""); // Clear search when closing
      // Reset flag after cleanup is complete
      isClosingRef.current = false;
    });
  }, [onClosePopup]);

  const handleClickNext = () => {
    handleSearch(selectedFilters);
    closeWindow(); // Replaced state update
  };

  const clearedFilters = useMemo(() => {
    return {
      ...selectedFilters,
      region: [],
      city: [],
      district: [],
      microdistrict: [],
      street: [],
      metroStation: [],
    };
  }, [selectedFilters]);

  const handleClickClear = () => {
    setSearchTitle("");
    setSelectedFilters(clearedFilters);
    handleSearch(clearedFilters, undefined, false);
    closeWindow();
  };

  const handleCitySearch = useCallback(
    (nameOfRegionOrCityFromEffect: string) => {
      // nameOfRegionOrCityFromEffect is expected to be the value from selectedFilters.region[0]
      // It's used to set both the city filter and to find its corresponding region via cityToRegionMap.

      const actualRegion = cityToRegionMap.get(nameOfRegionOrCityFromEffect);

      if (!actualRegion) {
        logger.warn(
          `No region found in cityToRegionMap for: ${nameOfRegionOrCityFromEffect}`,
        );
        return;
      }

      setSelectedFilters((prevFilters) => {
        const newRegionArray = [actualRegion];
        const newCityArray = [nameOfRegionOrCityFromEffect];
        const newDistrictArray: string[] = [];
        const newMicrodistrictArray: string[] = [];
        const newStreetArray: string[] = [];

        // Check if the relevant parts of the filter would actually change
        const regionChanged =
          JSON.stringify(prevFilters.region) !== JSON.stringify(newRegionArray);
        const cityChanged =
          JSON.stringify(prevFilters.city) !== JSON.stringify(newCityArray);
        const districtNeedClearing =
          prevFilters.district && prevFilters.district.length > 0;
        const microdistrictNeedClearing =
          prevFilters.microdistrict && prevFilters.microdistrict.length > 0;
        const streetNeedClearing =
          prevFilters.street && prevFilters.street.length > 0;

        if (
          !regionChanged &&
          !cityChanged &&
          !districtNeedClearing &&
          !microdistrictNeedClearing &&
          !streetNeedClearing
        ) {
          // If none of the parts that this function intends to modify are changing,
          // return the previous state to prevent a re-render cycle.
          return prevFilters;
        }
        logger.info(
          `Updating filters in handleCitySearch. Old: ${JSON.stringify({
            region: prevFilters.region,
            city: prevFilters.city,
            district: prevFilters.district,
          })}, New: ${JSON.stringify({ region: newRegionArray, city: newCityArray, district: newDistrictArray })}`,
        );
        return {
          ...prevFilters,
          region: newRegionArray,
          city: newCityArray,
          district: newDistrictArray,
          microdistrict: newMicrodistrictArray,
          street: newStreetArray,
          metroStation: [],
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cityToRegionMap, setSelectedFilters], // logger removed - it's stable and doesn't affect re-renders
  );

  // Build autocomplete options from regions/cities/districts
  const { items: autocompleteOptions } = useAutocompleteLocationOptions(
    regionsListData,
    locale,
  );

  // Filter autocomplete options based on search value
  const filteredAutocompleteOptions = useAutocompleteFilteredOptions(
    autocompleteOptions,
    searchValue,
  );

  const showRegions = useMemo(() => {
    const baseRegions = regionsListData || [];

    if (!searchValue.trim()) {
      return baseRegions;
    }

    const searchLower = searchValue.toLowerCase();
    return baseRegions.filter((region) => {
      const regionName = getName(region.name).toLowerCase();

      // Check if region name matches
      if (regionName.includes(searchLower)) {
        return true;
      }

      // Check if any city in this region matches
      return region.cities?.some((city) => {
        const cityName = getName(city.name).toLowerCase();
        if (cityName.includes(searchLower)) {
          return true;
        }

        // Check if any district in this city matches
        return city.districts?.some((district) => {
          const districtName = getName(district.name).toLowerCase();
          return districtName.includes(searchLower);
        });
      });
    });
  }, [regionsListData, searchValue, getName]);

  // State for collapsible sections
  const [openSections, setOpenSections] = useState({
    region: true,
    city: true,
    district: true,
    microdistrict: true,
    metroStation: true,
  });

  const initialSearchTerms = {
    city: "",
    district: "",
    microdistrict: "",
    metroStation: "",
  };

  // State for autocomplete search terms
  const [searchTerms, setSearchTerms] = useState(initialSearchTerms);

  // Handler for toggling section open/close
  const handleSectionToggle = (section: keyof typeof openSections) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Handler for autocomplete search term changes
  const handleSearchTermChange = (
    section: keyof typeof searchTerms,
    value: string,
  ) => {
    setSearchTerms((prev) => ({
      ...prev,
      [section]: value,
    }));
  };

  const availableCities = useMemo(() => {
    return (
      selectedFilters.region?.length ? selectedFilters.region : showRegions
    )?.flatMap((region) => {
      return (
        (typeof region === "string" ? regionMap.get(region) : region.cities) ||
        []
      );
    });
  }, [selectedFilters.region, showRegions, regionMap]);
  // logger.info("🚀 ~ availableCities:", availableCities);

  const availableDistricts = useMemo(
    () => selectedFilters.city?.flatMap((city) => cityMap.get(city) || []),
    [selectedFilters.city, cityMap],
  );

  const availableStreets = useMemo(
    () =>
      selectedFilters.district?.flatMap(
        (district) => districtStreetMap.get(district) || [],
      ),
    [selectedFilters.district, districtStreetMap],
  );

  const availableMicrodistricts = useMemo(
    () =>
      selectedFilters.district?.flatMap(
        (district) => districtMicrodistrictMap.get(district) || [],
      ),
    [selectedFilters.district, districtMicrodistrictMap],
  );

  const availableMetroStations = useMemo(
    () =>
      selectedFilters.city?.flatMap(
        (city) => cityMapWithMetroStation.get(city) || [],
      ),
    [selectedFilters.city, cityMapWithMetroStation],
  );

  // Filtered cities based on search term
  const filteredCities = useMemo(() => {
    if (!searchTerms.city) return availableCities;
    return (
      availableCities?.filter((city) =>
        getName(city.name, false)
          .toLowerCase()
          .includes(searchTerms.city.toLowerCase()),
      ) || []
    );
  }, [availableCities, searchTerms.city, getName]);

  // Filtered districts based on search term
  const filteredDistricts = useMemo(() => {
    if (!searchTerms.district) return availableDistricts;
    return (
      availableDistricts?.filter((district) =>
        getName(district.name, false)
          .toLowerCase()
          .includes(searchTerms.district.toLowerCase()),
      ) || []
    );
  }, [availableDistricts, searchTerms.district, getName]);

  // Filtered microdistricts based on search term
  const filteredMicrodistricts = useMemo(() => {
    if (!searchTerms.microdistrict) return availableMicrodistricts;
    return (
      availableMicrodistricts?.filter((microdistrict) =>
        getName(microdistrict.name, false)
          .toLowerCase()
          .includes(searchTerms.microdistrict.toLowerCase()),
      ) || []
    );
  }, [availableMicrodistricts, searchTerms.microdistrict, getName]);

  // Filtered metro stations based on search term
  const filteredMetroStations = useMemo(() => {
    if (!searchTerms.metroStation) return availableMetroStations;
    return (
      availableMetroStations?.filter((metroStation) =>
        getName(metroStation.name, false)
          .toLowerCase()
          .includes(searchTerms.metroStation.toLowerCase()),
      ) || []
    );
  }, [availableMetroStations, searchTerms.metroStation, getName]);

  const metroStationsByCity = useMemo(() => {
    const result: Map<
      string,
      {
        localizedCityName: string;
        metroStations: MetroStation[];
      }
    > = new Map();

    if (!availableCities?.length || !filteredMetroStations?.length) {
      return result;
    }

    for (const city of availableCities) {
      if (!city.metroStations?.length) continue;

      const cityMetroNames = new Set(
        city.metroStations.map((s) => getName(s.name)),
      );

      const metroStations = filteredMetroStations
        .filter((s) => cityMetroNames.has(getName(s.name)))
        .sort((a, b) => a.name[locale].localeCompare(b.name[locale]));

      if (metroStations.length > 0) {
        result.set(getName(city.name), {
          localizedCityName: getName(city.name, false),
          metroStations,
        });
      }
    }

    return result;
  }, [availableCities, filteredMetroStations, getName, locale]);

  const isShowMainActionsSection = useMemo(() => {
    return !searchValue.trim() && filteredAutocompleteOptions.length === 0;
  }, [searchValue, filteredAutocompleteOptions]);

  // Handle autocomplete item selection
  const handleAutocompleteSelect = (item: AutocompleteItem) => {
    logger.info("Autocomplete item selected:", item);

    setSelectedFilters((prev) => ({
      ...prev,
      region: item.regionName ? [item.regionName] : [],
      city: item.cityName ? [item.cityName] : [],
      district: item.districtName ? [item.districtName] : [],
      microdistrict: item.microdistrictName ? [item.microdistrictName] : [],
      street: item.streetName ? [item.streetName] : [],
      metroStation: item.metroStationName ? [item.metroStationName] : [],
    }));

    // Apply the search immediately
    const filtersForApi = {
      ...selectedFilters,
      region: item.regionName ? [item.regionName] : [],
      city: item.cityName ? [item.cityName] : [],
      district: item.districtName ? [item.districtName] : [],
      microdistrict: item.microdistrictName ? [item.microdistrictName] : [],
      street: item.streetName ? [item.streetName] : [],
      metroStation: item.metroStationName ? [item.metroStationName] : [],
    };

    handleSearch(filtersForApi);
    setSearchValue(""); // Clear search after selection
    closeWindow();
  };

  const handleApply = () => {
    const fullCoincidence = filteredAutocompleteOptions.find(
      (item) =>
        item.title.trim().toLowerCase() === searchValue.trim().toLowerCase(),
    );

    if (filteredAutocompleteOptions.length === 1 || fullCoincidence) {
      handleAutocompleteSelect(
        fullCoincidence ?? filteredAutocompleteOptions[0],
      );
      return;
    }

    logger.info("Applying location filter:", selectedFilters);

    // Create a deep copy of selectedFilters to avoid modifying the original state directly
    const filtersForApi = JSON.parse(JSON.stringify(selectedFilters));

    logger.info(
      "Filters being sent to API (raw filtersForApi object):",
      filtersForApi,
    );
    logger.info(
      "Filters being sent to API (JSON.stringify for detailed inspection):",
      JSON.stringify(filtersForApi, null, 2),
    );
    handleSearch(filtersForApi);
    closeWindow();
  };

  return (
    <>
      <div
        style={style}
        className={cn(styles.categoryList)}
      >
        <div className={cn("search-panel__modal-header", "md:hidden")}>
          <h1 className="search-panel__modal-title">
            {t("Location selection")}
          </h1>
          <CloseButton
            size={25}
            className={"p-2"}
            onClick={closeWindow}
          />
        </div>

        {searchValue.trim() && (
          <AutocompleteInputSectionForModal
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            setSelectedFilters={setSelectedFilters}
            handleAutocompleteSelect={handleAutocompleteSelect}
            clearedFilters={clearedFilters}
            filteredAutocompleteOptions={filteredAutocompleteOptions}
            searchInputRef={searchInputRef}
            placeholder={`${t("Search locations")}...`}
          />
        )}

        <div
          className={cn(
            "w-full max-w-full overflow-y-auto flex flex-col gap-1 items-start p-0 flex-1",
            !isShowMainActionsSection && "mb-4",
          )}
        >
          {!searchValue.trim() && (
            <AutocompleteInputSectionForModal
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              setSelectedFilters={setSelectedFilters}
              handleAutocompleteSelect={handleAutocompleteSelect}
              clearedFilters={clearedFilters}
              filteredAutocompleteOptions={filteredAutocompleteOptions}
              searchInputRef={searchInputRef}
              placeholder={`${t("Search locations")}...`}
            />
          )}

          {/* Show autocomplete results when searching on mobile */}
          {!isShowMainActionsSection && (
            <div className="block md:hidden w-full">
              <AutocompleteList
                items={filteredAutocompleteOptions}
                searchValue={searchValue}
                listTitle={t("Enter location")}
                onItemClick={handleAutocompleteSelect}
                noResultsMessage={
                  <div className="text-center py-8 text-default-500">
                    {t("search.table.noResults")}
                  </div>
                }
              />
            </div>
          )}

          {/* Show regular location grid when not searching OR on desktop */}
          {(isShowMainActionsSection || !isMobile) && (
            <>
              <RecommendedCities
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                regionsListData={regionsListData}
                closeWindow={closeWindow}
                className={cn("pt-0 md:pt-6")}
              />
              <div
                className={cn("md:overflow-x-auto", styles.locationMainWrapper)}
              >
                {/* Regions */}
                <h3
                  className={
                    "md:border-r select-none md:cursor-default cursor-pointer !font-normal"
                  }
                  style={{}}
                >
                  {t("Region")}
                  <IconArrow
                    className={cn(
                      "inline-block transition-transform md:hidden ml-2 text-red",
                      openSections.region
                        ? "rotate-[90deg]"
                        : "rotate-[270deg]",
                    )}
                  />
                </h3>
                {openSections.region && (
                  <div className={cn(styles.locationWrapper, "p-[2rem]")}>
                    {(regionsListData ?? showRegions).map((region) => {
                      const regionName = getName(region?.name);
                      const isChecked =
                        selectedFilters.region?.includes(regionName);

                      // Debug logging - always log for debugging
                      logger.info(
                        `[SearchLocationNew] Checking region "${regionName}":`,
                        {
                          regionName,
                          selectedRegions: selectedFilters.region,
                          isChecked,
                          regionNameObject: region.name,
                          regionNameFromData: getName(region.name),
                          exactMatch:
                            selectedFilters.region?.includes(regionName),
                        },
                      );

                      return (
                        <label
                          key={region.name[defaultLocale]}
                          className={styles.nestedSubcategory}
                        >
                          <input
                            className={cn(styles.checkbox, styles.inputChecked)}
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) =>
                              toggleSelection(
                                e,
                                selectedFilters.region!,
                                region.name.en,
                                EnumLocation.region,
                              )
                            }
                          />
                          <span className={styles.locationTitle}>
                            {getName(region?.name, false)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {/* Cities */}
                <h3
                  className={
                    "md:border-r select-none md:cursor-default cursor-pointer !font-normal"
                  }
                  style={{}}
                >
                  {t("City")}
                  <IconArrow
                    className={cn(
                      "inline-block transition-transform md:hidden ml-2 text-red",
                      openSections.city ? "rotate-[90deg]" : "rotate-[270deg]",
                    )}
                  />
                </h3>
                <div className={styles.locationWrapper}>
                  {openSections.city &&
                    selectedFilters &&
                    selectedFilters.region &&
                    availableCities &&
                    availableCities.length > 0 && (
                      <div className="p-[2rem]">
                        <div className="mb-4">
                          <FormInput
                            name="citySearch"
                            placeholder={`${t("Search cities")}...`}
                            value={searchTerms.city}
                            onChange={(e) =>
                              handleSearchTermChange(
                                "city",
                                e.currentTarget.value,
                              )
                            }
                            startContent={
                              <Search className="w-9 h-9 pointer-events-none text-[#272727]" />
                            }
                          />
                        </div>
                        {(selectedFilters.region.length
                          ? selectedFilters.region
                          : showRegions
                        ).map((regionTemp) => {
                          const regionName: string =
                            typeof regionTemp === "string"
                              ? regionTemp
                              : regionTemp.name.en;
                          const region = regionsListData?.find((r) => {
                            return getName(r.name) === regionName;
                          });

                          if (!region?.cities?.length) {
                            return null;
                          }

                          const citiesToShow =
                            filteredCities?.filter((city) =>
                              region.cities?.some(
                                (regionCity) =>
                                  getName(regionCity.name) ===
                                  getName(city.name),
                              ),
                            ) || [];

                          if (citiesToShow.length === 0) {
                            return null;
                          }

                          return (
                            <div
                              className={"space-y-[0.5rem] mb-4"}
                              key={regionName}
                            >
                              <h4>{getName(region.name, false)}</h4>
                              {citiesToShow.map((city) => {
                                return (
                                  <label
                                    key={city.name[defaultLocale]}
                                    className={styles.nestedSubcategory}
                                  >
                                    <input
                                      className={cn(
                                        styles.checkbox,
                                        styles.inputCheckedSub,
                                      )}
                                      type="checkbox"
                                      checked={selectedFilters.city?.includes(
                                        getName(city.name),
                                      )}
                                      onChange={(e) =>
                                        toggleSelection(
                                          e,
                                          selectedFilters.city!,
                                          getName(city.name),
                                          EnumLocation.city,
                                        )
                                      }
                                    />
                                    <span className={styles.locationSubTitle}>
                                      {getName(city.name, false)}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    )}
                </div>
                {/* District */}
                <h3
                  className={
                    "md:border-r select-none md:cursor-default cursor-pointer !font-normal"
                  }
                  style={{}}
                >
                  {t("District")}
                  <IconArrow
                    className={cn(
                      "inline-block transition-transform md:hidden ml-2 text-red",
                      openSections.district
                        ? "rotate-[90deg]"
                        : "rotate-[270deg]",
                    )}
                  />
                </h3>
                <div className={styles.locationWrapper}>
                  {openSections.district &&
                    selectedFilters &&
                    selectedFilters.region &&
                    selectedFilters.city &&
                    selectedFilters.region.length > 0 &&
                    selectedFilters.city.length > 0 &&
                    availableDistricts &&
                    availableDistricts.length > 0 && (
                      <div className="p-[2rem]">
                        <div className="mb-4">
                          <FormInput
                            name="districtSearch"
                            placeholder={`${t("Search districts")}...`}
                            value={searchTerms.district}
                            onChange={(e) =>
                              handleSearchTermChange(
                                "district",
                                e.currentTarget.value,
                              )
                            }
                            startContent={
                              <Search className="w-9 h-9 pointer-events-none text-[#272727]" />
                            }
                          />
                        </div>
                        {selectedFilters.city.map((cityName: string) => {
                          const city = availableCities?.find((c) => {
                            return getName(c.name) === cityName;
                          });

                          if (!city?.districts?.length) {
                            return null;
                          }

                          const districtsToShow =
                            filteredDistricts?.filter((district) =>
                              city.districts?.some(
                                (cityDistrict) =>
                                  getName(cityDistrict.name) ===
                                  getName(district.name),
                              ),
                            ) || [];

                          if (districtsToShow.length === 0) {
                            return null;
                          }

                          return (
                            <div
                              className={"space-y-[0.5rem] mb-4"}
                              key={cityName}
                            >
                              {selectedFilters?.city?.length && (
                                <h4>{getName(city.name, false)}</h4>
                              )}
                              {districtsToShow.map((district) => {
                                return (
                                  <label
                                    key={district.name[defaultLocale]}
                                    className={styles.nestedSubcategory}
                                  >
                                    <input
                                      className={cn(
                                        styles.checkbox,
                                        styles.inputCheckedSub,
                                      )}
                                      type="checkbox"
                                      checked={selectedFilters.district?.includes(
                                        getName(district.name),
                                      )}
                                      onChange={(e) =>
                                        toggleSelection(
                                          e,
                                          selectedFilters.district!,
                                          getName(district.name),
                                          EnumLocation.district,
                                        )
                                      }
                                    />
                                    <span className={styles.locationSubTitle}>
                                      {getName(district.name, false)}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    )}
                </div>
                {/* Microdistrict */}
                <h3
                  className={
                    "md:border-r select-none md:cursor-default cursor-pointer !font-normal"
                  }
                  style={{}}
                >
                  {t("Microdistrict")}
                  <IconArrow
                    className={cn(
                      "inline-block transition-transform md:hidden ml-2 text-red",
                      openSections.microdistrict
                        ? "rotate-[90deg]"
                        : "rotate-[270deg]",
                    )}
                  />
                </h3>
                <div className={styles.locationWrapper}>
                  {openSections.microdistrict &&
                    selectedFilters &&
                    selectedFilters.region &&
                    selectedFilters.city &&
                    selectedFilters.district &&
                    selectedFilters.region.length > 0 &&
                    selectedFilters.city.length > 0 &&
                    selectedFilters.district.length > 0 &&
                    availableMicrodistricts &&
                    availableMicrodistricts.length > 0 && (
                      <div className="p-[2rem]">
                        <div className="mb-4">
                          <FormInput
                            name="microdistrictSearch"
                            placeholder={`${t("Search microdistricts")}...`}
                            value={searchTerms.microdistrict}
                            onChange={(e) =>
                              handleSearchTermChange(
                                "microdistrict",
                                e.currentTarget.value,
                              )
                            }
                            startContent={
                              <Search className="w-9 h-9 pointer-events-none text-[#272727]" />
                            }
                          />
                        </div>
                        {selectedFilters.district.map(
                          (districtName: string) => {
                            const district = availableDistricts?.find((d) => {
                              return getName(d.name) === districtName;
                            });

                            if (!district?.microdistricts?.length) {
                              return null;
                            }

                            const microdistrictsToShow =
                              filteredMicrodistricts?.filter((microdistrict) =>
                                district.microdistricts?.some(
                                  (districtMicrodistrict) =>
                                    getName(districtMicrodistrict.name) ===
                                    getName(microdistrict.name),
                                ),
                              ) || [];

                            if (microdistrictsToShow.length === 0) {
                              return null;
                            }

                            return (
                              <div
                                className={"space-y-[0.5rem] mb-4"}
                                key={districtName}
                              >
                                {selectedFilters?.district?.length && (
                                  <h4>{getName(district.name, false)}</h4>
                                )}
                                {microdistrictsToShow.map((microdistrict) => {
                                  return (
                                    <label
                                      key={microdistrict.name[defaultLocale]}
                                      className={styles.nestedSubcategory}
                                    >
                                      <input
                                        className={cn(
                                          styles.checkbox,
                                          styles.inputCheckedSub,
                                        )}
                                        type="checkbox"
                                        checked={selectedFilters.microdistrict?.includes(
                                          getName(microdistrict.name),
                                        )}
                                        onChange={(e) =>
                                          toggleSelection(
                                            e,
                                            selectedFilters.microdistrict!,
                                            getName(microdistrict.name),
                                            EnumLocation.microdistrict,
                                          )
                                        }
                                      />
                                      <span className={styles.locationSubTitle}>
                                        {getName(microdistrict.name, false)}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            );
                          },
                        )}
                      </div>
                    )}
                </div>
                {/* Metro Stations */}
                <h3
                  className={
                    "select-none md:cursor-default cursor-pointer !font-normal"
                  }
                  style={{}}
                >
                  {t("Metro Station")}
                  <IconArrow
                    className={cn(
                      "inline-block transition-transform md:hidden ml-2 text-red",
                      openSections.metroStation
                        ? "rotate-[90deg]"
                        : "rotate-[270deg]",
                    )}
                  />
                </h3>
                <div className={styles.locationWrapper}>
                  {openSections.metroStation &&
                    selectedFilters &&
                    selectedFilters.region &&
                    selectedFilters.city &&
                    selectedFilters.region.length > 0 &&
                    selectedFilters.city.length > 0 &&
                    availableMetroStations &&
                    availableMetroStations.length > 0 && (
                      <div className="p-[2rem]">
                        <div className="mb-4">
                          <FormInput
                            name="metroStationSearch"
                            placeholder={`${t("Search metro stations")}...`}
                            value={searchTerms.metroStation}
                            onChange={(e) =>
                              handleSearchTermChange(
                                "metroStation",
                                e.currentTarget.value,
                              )
                            }
                            startContent={
                              <Search className="w-9 h-9 pointer-events-none text-[#272727]" />
                            }
                          />
                        </div>
                        {selectedFilters.city.map((cityName: string) => {
                          const { localizedCityName, metroStations } =
                            metroStationsByCity.get(cityName) ?? {};

                          if (!metroStations) return null;

                          return (
                            <div
                              className={"space-y-[0.5rem] mb-4"}
                              key={cityName}
                            >
                              <h4>{localizedCityName}</h4>
                              {metroStations.map((metroStation) => {
                                return (
                                  <label
                                    key={
                                      metroStation._id ||
                                      `metro-${getName(metroStation.name)}`
                                    }
                                    className={styles.nestedSubcategory}
                                  >
                                    <input
                                      className={cn(
                                        styles.checkbox,
                                        styles.inputCheckedSub,
                                      )}
                                      type="checkbox"
                                      checked={selectedFilters.metroStation?.includes(
                                        getName(metroStation.name),
                                      )}
                                      onChange={(e) =>
                                        toggleSelection(
                                          e,
                                          selectedFilters.metroStation!,
                                          getName(metroStation.name),
                                          EnumLocation.metroStation,
                                        )
                                      }
                                    />
                                    <span className={styles.locationSubTitle}>
                                      {getName(metroStation.name, false)}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    )}
                </div>
              </div>
            </>
          )}
        </div>
        {(isShowMainActionsSection || !isMobile) && (
          <div className={cn(styles.nextButtonWrapper)}>
            <Button
              className="w-full md:w-auto min-w-[15rem] xl:min-w-[22rem]"
              size="md"
              color="white"
              radius="md"
              onClick={handleClickClear}
            >
              {t("Clear")}
            </Button>
            <Button
              className="w-full md:w-auto min-w-[15rem] xl:min-w-[22rem]"
              size="md"
              color="red"
              radius="md"
              onClick={handleApply}
            >
              {t("Apply")}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
export default SearchLocation;
